import { useTranslation } from "react-i18next";
import type { ProjectSummary } from "../../../api/projectsApi";
import type { ProjectRuleName, ProjectRules } from "../../../api/settingsApi";
import HoverHint from "../../../ui/feedback/HoverHint";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { ruleName } from "../settingsTexts";
import LibraryPinningField from "./LibraryPinningField";
import SecondCuratorRule from "./SecondCuratorRule";

/**
 * The one own-change row DMW enforces today: tools and skills with code (ADR-0028; the other
 * rows wait for ADR-0026 slice 2 and stay hidden, redesign R3-2 "Own changes").
 */
const ENFORCED_CHANGE_KIND = "executable";

interface ProjectRulesFieldsProps {
  rules: ProjectRules;
  /** The projects a library may list as pinning it; omitted for the installation's defaults. */
  pinningChoices?: readonly ProjectSummary[];
  /** Omitted: the rules are shown read-only. */
  onChange?: (rules: ProjectRules) => void;
}

/**
 * A project's rules as far as they act today (accounts.md ST-1, redesign R3-2 "Project rules"):
 * whether tools and skills with code need a second curator (ADR-0028), publishing releases to
 * the installation's ResearchSpace (F5), which projects may pin it as a library (JD-2) and
 * whether the installation merges its own system changes (MA-C1, built provisionally). Rules
 * that don't act yet stay hidden, their stored values untouched. The project's settings and the
 * installation's defaults for new projects use it.
 */
export default function ProjectRulesFields({ rules, pinningChoices, onChange }: ProjectRulesFieldsProps) {
  const enforced = rules.mergeRules[ENFORCED_CHANGE_KIND];

  return (
    <div className="space-y-4 text-sm">
      {enforced === undefined ? null : (
        <SecondCuratorRule rule={enforced} onChange={onChange && ((rule) => { onChange({ ...rules, mergeRules: { ...rules.mergeRules, [ENFORCED_CHANGE_KIND]: rule } }); })} />
      )}

      <ToggleRule name="publish_to_researchspace" hint="publishToResearchSpaceHint" checked={rules.publishToResearchSpace}
        onChange={onChange && ((on) => { onChange({ ...rules, publishToResearchSpace: on }); })} />

      <ToggleRule name="installation_merges_system_changes" hint="installationMergesSystemChangesHint" checked={rules.installationMergesSystemChanges}
        onChange={onChange && ((on) => { onChange({ ...rules, installationMergesSystemChanges: on }); })} />

      <LibraryPinningField pinningProjects={rules.pinningProjects} choices={pinningChoices}
        onChange={onChange === undefined ? undefined : (pinningProjects) => { onChange({ ...rules, pinningProjects }); }} />
    </div>
  );
}

interface ToggleRuleProps {
  name: ProjectRuleName;
  /** The key under `projects.settings.rules` of what the rule does. */
  hint: string;
  checked: boolean;
  /** Omitted: shown read-only. */
  onChange: ((on: boolean) => void) | undefined;
}

/** A rule that is on or off, with what it does on hover or focus. */
function ToggleRule({ name, hint, checked, onChange }: ToggleRuleProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <OptionLabel>
        <input type="checkbox" checked={checked} disabled={onChange === undefined} onChange={(event) => { onChange?.(event.target.checked); }} />
        {ruleName(t, name)}
      </OptionLabel>
      <HoverHint name={ruleName(t, name)}>{t(`projects.settings.rules.${hint}`)}</HoverHint>
    </div>
  );
}
